export interface SavedLoginPrompt {
    id: string;
    sortOrder: number;
    enabled: boolean;
}

export interface PickLoginPromptInput {
    nowMs: number;
    gapMinutes: number;
    lastClosedAtMs: number | null;
    /**
     * True while an on-login prompt is still on screen.
     * The gap timer must not run until the shopper finishes that prompt.
     */
    slotOpen: boolean;
    /** Cookie bar, a quiet route, or some other dialog is holding the screen. */
    blocked: boolean;
    prompts: readonly { id: string; sortOrder: number; enabled: boolean }[];
    waitingIds: readonly string[];
}

export type PickLoginPromptResult =
    | { kind: 'idle' }
    | { kind: 'wait'; resumeAtMs: number }
    | { kind: 'grant'; id: string };

export function mergeLoginPromptOrder(
    registry: readonly { id: string; defaultSort: number }[],
    saved: readonly SavedLoginPrompt[]
): { id: string; sortOrder: number; enabled: boolean }[] {
    const byId = new Map(saved.map((row) => [row.id, row]));
    return registry.map((item) => {
        const row = byId.get(item.id);
        return {
            id: item.id,
            sortOrder: row?.sortOrder ?? item.defaultSort,
            enabled: row?.enabled ?? true,
        };
    });
}

/** Sell, edit, checkout, and auth forms. A prompt waits until the shopper leaves. */
export function isLoginPromptQuietPath(pathname: string): boolean {
    const path = (pathname || '/').toLowerCase();
    return (
        path === '/sell' ||
        path.startsWith('/sell/') ||
        path.startsWith('/edit-product') ||
        path.startsWith('/checkout') ||
        path === '/login' ||
        path === '/register'
    );
}

export function pickLoginPrompt(input: PickLoginPromptInput): PickLoginPromptResult {
    if (input.slotOpen) return { kind: 'idle' };

    const gapMinutes = Number.isFinite(input.gapMinutes) ? Math.max(1, input.gapMinutes) : 3;
    const gapMs = gapMinutes * 60_000;
    if (input.lastClosedAtMs != null && Number.isFinite(input.lastClosedAtMs)) {
        const resumeAtMs = input.lastClosedAtMs + gapMs;
        if (input.nowMs < resumeAtMs) return { kind: 'wait', resumeAtMs };
    }

    if (input.blocked) return { kind: 'idle' };

    const waiting = new Set(input.waitingIds);
    const next = input.prompts
        .filter((prompt) => prompt.enabled && waiting.has(prompt.id))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))[0];

    if (!next) return { kind: 'idle' };
    return { kind: 'grant', id: next.id };
}
